import { Breadcrumbs } from '@own/fds-react';

import React, { useContext, useEffect, useState } from 'react';
import { useAppInformationContent } from '../../../components/sections/breadcrumbs/hook/use-breadcrumb-content';
import ServerContext from '../../../contexts/serverContext';
import { BlueCruiseFaqs } from '../../components/bluecruise-faqs/bluecruise-faqs';
import useSeoConfig from '../../../hooks/use-seo-config';
import { useFAQsAccordionNoDisclaimer } from '../../../views/sync-maps-updates-view/hooks/use-faqs-accordion-no-disclaimer';
import {
    useRecallsRecentLinksContent,
    useRecallsUnitedContent,
} from './hooks/use-recalls-united-content';
import { PageNotFoundView } from '../../../views';
import './recalls-united.scss';
import SupportFdsWrapper from '../../components/support-fds-wrapper/support-fds-wrapper';
import RecallsInformationSection, {
    scrollToRecallsResults,
} from '../../components/recalls-information-section/recalls-information-section';
import { RecallSignInAndVinInput } from '../../components/recalls-vin-sign-in/recall-sign-in-and-vin-input';
import { VehicleAttributes } from '../../../models/vehicle-attributes';
import { RecentRecallsLinks } from '../../components/recent-recalls-links/recent-recalls-links';
import FMDrawer from '../../../components/common/fm-drawer/fm-drawer';
import { RecallVinInput } from '../../components/recalls-vin-sign-in/recall-vin-input';
import useUserProfile from '../../../hooks/use-user-profile';
import useFooterDisplay from '../../../hooks/use-footer-display';

import {
    AuthenticationState,
    useAuthentication,
} from '../../../hooks/use-authentication';
import { useRecallData } from '../../../components/sections/vehicle-portal/hooks/use-recall-data';
import ProfileService from '../../../services/profile-service/profile-service';
import { useAnalytics } from '../../../hooks/use-analytics';
import isWebviewCheck from '../../../components/utils/webviewCheck/webviewCheck';
import VehicleTypeService from '../../../services/vehicle-type-service/vehicle-type-service';
import { useHistory, useParams } from 'react-router-dom';
import { getVinFromLocalStorageIfExists } from '../../../components/sections/pre-imagine-components/pre-imagine-vehicle-selector/pre-imagine-vin-selector/pre-imagine-vin-selector';
import localStorageWrapper from '../../../components/utils/local-storage-wrapper/localStorageWrapper';
import { isItAVin } from '../recalls-details/recalls-details';
import { getAemVehicleModelData } from '../../../hooks/use-aem-vehicle-model-data';
import { VehicleYmm } from '../../../models/vehicle-ymm';
import siteMapService from '../../services/site-map-service/site-map-service';
import { ActivityIndicator } from '../../../components/common/activity-indicator/activity-indicator';
import { format } from 'date-fns';
import { PreImagineEscapeHatch } from '../../../components/sections/pre-imagine-components/pre-imagine-escape-hatch/pre-imagine-escape-hatch';
import { useHelpCardsContent } from '../../../views/page-not-found-view/hooks/use-help-cards-content';
import { useSearchContent } from '../../../components/sections/search-bar/hooks/use-search-content';

export interface RecallsUnitedVehicleData {
    loading: boolean;
    error: string;
    vehicleAttributes: VehicleAttributes;
}
const RecallsUnited = () => {
    const { vinOrModel, year } = useParams<{
        vinOrModel: string;
        year: string;
    }>();
    const paramVin = isItAVin(vinOrModel) ? vinOrModel : '';
    const verifiedYear =
        parseInt(year, 10) > 1900 && parseInt(year, 10) < 2100 ? year : '';
    const model = verifiedYear && !paramVin ? vinOrModel : '';

    const serverContext = useContext(ServerContext);
    const currentRoot = serverContext.root
        ? serverContext.root.substring(0, serverContext.root.length - 1)
        : '';
    useSeoConfig('recalls-details', 'recalls');
    const appInformationContent = useAppInformationContent();
    const recallsContent = useRecallsUnitedContent();
    const helpCardsContent = useHelpCardsContent();
    const searchContent = useSearchContent();
    const faqs = useFAQsAccordionNoDisclaimer(
        'recalls',
        'recent-recalls-united-links'
    );
    const recallsRecentLinksContent = useRecallsRecentLinksContent();
    const profile = useUserProfile();
    const [authenticationState] = useAuthentication();
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [currentVehicle, setCurrentVehicle] = useState<
        RecallsUnitedVehicleData
    >({
        loading: false,
        error: '',
        vehicleAttributes: {} as VehicleAttributes,
    } as RecallsUnitedVehicleData);
    const [vehiclesData, setVehiclesData] = useState<VehicleAttributes[]>([]);
    const [vinPageLoadEventFiredFor, setVinPageLoadEventFiredFor] = useState<
        string
    >('');
    const [ymmPageLoadEventFiredFor, setYmmPageLoadEventFiredFor] = useState<
        string
    >('');
    const setDrawerOpen = () => {
        setOpenDrawer(true);
    };
    const [fireEvents] = useAnalytics();
    const recallsFaqEvent = (panelTitle: string) => {
        const selfHelpFaq = panelTitle || 'no question';
        fireEvents('recalls-faq', undefined, { selfHelpFaq }, false);
    };
    const recallsData = useRecallData(
        currentVehicle.vehicleAttributes?.vin,
        model,
        verifiedYear,
        scrollToRecallsResults
    );
    const profileService = new ProfileService();

    const isLoggedIn =
        authenticationState === AuthenticationState.Authenticated;
    const breadcrumbCondition =
        recallsContent?.breadcrumbLabel &&
        (appInformationContent?.breadcrumbStart ||
            appInformationContent?.applicationName);
    const isWebview = isWebviewCheck();
    const webviewVin = serverContext.webviewData?.vin;
    const webViewPageType = serverContext.recallsType;
    const getCurrentDate = () => format(new Date(), 'MM/dd/yyyy');
    const showRecalls = webViewPageType !== 'csp';
    const showCsp = webViewPageType !== 'recalls';
    const vinFromStorage = getVinFromLocalStorageIfExists();
    const history = useHistory();
    const initialVin = paramVin || webviewVin || vinFromStorage;
    const [landWithVehicleLoader, setLandWithVehicleLoader] = useState<boolean>(
        !!(initialVin || model || profile)
    );
    const recallsPath = siteMapService
        .getPathFromRouterConfigs(
            serverContext.currentLanguageRegionCode,
            serverContext.brand,
            'RecallsUnited',
            []
        )
        .replace('/:vinOrModel?/:year?/', '');
    const handleAuthVehicleChange = (
        title: string,
        index: number,
        value: string | undefined
    ) => {
        localStorage.removeItem('USER_VIN');
        if (value === 'Add Vehicle' && recallsContent?.addVehicleCtaLink) {
            (window as any).location = recallsContent.addVehicleCtaLink;
        } else {
            setCurrentVehicle({
                ...currentVehicle,
                vehicleAttributes: vehiclesData[index],
            });
            if (profile) {
                profileService.persistVehicleSelection(
                    profile.profile.email,
                    vehiclesData[index]?.vin
                );
            }
            history.push(recallsPath);
        }
    };
    const authActions = () => {
        const lastSelctedVin = profileService.getUserPreferences(
            profile?.profile?.email
        )?.lastSelectedVin;
        const vehicles = [...(profile?.vehicles ?? [])]
            .sort(
                (vehicle1, vehicle2) =>
                    parseInt(vehicle2.modelYear, 10) -
                    parseInt(vehicle1.modelYear, 10)
            )
            .map(vehicleDetail => {
                return {
                    year: parseInt(vehicleDetail.modelYear, 10),
                    make: vehicleDetail.make,
                    model: vehicleDetail.modelName,
                    vin: vehicleDetail.vin,
                    ownerState: vehicleDetail.ownerState,
                    registrationNo: vehicleDetail.licenseplate,
                    fuelType: vehicleDetail.fuelType,
                    engineType: vehicleDetail.engineType,
                    warrantyStartDate: vehicleDetail.warrantyStartDate,
                };
            });
        setLandWithVehicleLoader(false);

        if (lastSelctedVin) {
            const selectedVehicle = vehicles.find(
                vehicle => vehicle.vin === lastSelctedVin
            );
            selectedVehicle &&
                setCurrentVehicle({
                    loading: false,
                    error: '',
                    vehicleAttributes: selectedVehicle,
                });
        } else {
            setCurrentVehicle({
                loading: false,
                error: '',
                vehicleAttributes: vehicles[0],
            });
        }
        setVehiclesData(vehicles);
    };
    const initialVinActions = () => {
        setCurrentVehicle({
            loading: true,
            error: '',
            vehicleAttributes: {} as VehicleAttributes,
        });

        if (paramVin) {
            localStorageWrapper.setItem('USER_VIN', paramVin);
        }
        setLandWithVehicleLoader(false);
        new VehicleTypeService()
            .request(
                initialVin,
                serverContext.currentLanguageRegionCode,
                serverContext.current3LetterCountryCode?.toUpperCase()
            )
            .then(res => {
                if (res?.vehicleData) {
                    const attributes: VehicleAttributes = {
                        modelYear: res.vehicleData.year,
                        modelName: res.vehicleData.model,
                        make: res.vehicleData.make,
                        vin: initialVin,
                        year: +res.vehicleData.year,
                        model: res.vehicleData.model,
                    };

                    setCurrentVehicle({
                        loading: false,
                        error: '',
                        vehicleAttributes: attributes,
                    });
                } else {
                    setCurrentVehicle({
                        loading: false,
                        error: 'Vehicle not found',
                        vehicleAttributes: {} as VehicleAttributes,
                    });
                }
            })
            .catch(error => {
                setCurrentVehicle({
                    loading: false,
                    error: error.message,
                    vehicleAttributes: {} as VehicleAttributes,
                });
                console.error(error);
            });
    };
    const yearAndModelActions = () => {
        setLandWithVehicleLoader(false);
        setCurrentVehicle({
            loading: true,
            error: '',
            vehicleAttributes: {} as VehicleAttributes,
        });

        getAemVehicleModelData(
            serverContext.brand,
            serverContext.currentRegionCode,
            serverContext.currentLanguageRegionCode,
            year,
            model,
            'SEO_KEY'
        ).then((data: VehicleYmm | undefined) => {
            if (data !== undefined) {
                const attributes: VehicleAttributes = {
                    modelYear: data.year.toString(),
                    modelName: data.model,
                    make: data.make,
                    vin: '',
                    year: data.year,
                    model: data.model,
                };
                setCurrentVehicle({
                    loading: false,
                    error: '',
                    vehicleAttributes: attributes,
                });
            } else {
                const attributes: VehicleAttributes = {
                    modelYear: verifiedYear,
                    modelName: model,
                    make: '',
                    vin: '',
                    year: +verifiedYear,
                    model: model,
                };
                setCurrentVehicle({
                    loading: false,
                    error: '',
                    vehicleAttributes: attributes,
                });
            }
        });
    };
    useEffect(() => {
        if (initialVin) {
            initialVinActions();
        } else if (verifiedYear && model) {
            yearAndModelActions();
        }
        if (profile) {
            authActions();
        }
    }, [vinOrModel, year, profile]);

    useEffect(() => {
        const currentMakeModelYear = `${currentVehicle?.vehicleAttributes?.make}:${currentVehicle?.vehicleAttributes?.model}:${currentVehicle?.vehicleAttributes?.year}`;
        if (
            currentVehicle?.vehicleAttributes &&
            (vinPageLoadEventFiredFor !==
                currentVehicle.vehicleAttributes.vin ||
                ymmPageLoadEventFiredFor !== currentMakeModelYear) &&
            currentVehicle.vehicleAttributes.year > 0
        ) {
            fireEvents(
                ['owner-recalls-details'],
                undefined,
                {
                    recallsDetails: 'recalls-details',
                    ymm: currentVehicle.vehicleAttributes,
                },
                false
            );
            setVinPageLoadEventFiredFor(
                currentVehicle.vehicleAttributes.vin || ''
            );
            setYmmPageLoadEventFiredFor(currentMakeModelYear || '');
        }
    }, [currentVehicle?.vehicleAttributes]);
    useFooterDisplay([recallsContent]);
    return (
        <>
            {recallsContent ? (
                <SupportFdsWrapper className="recalls-united-container">
                    <div>
                        {!isWebview && (
                            <>
                                <FMDrawer
                                    className={
                                        'recalls-auth-vin-selector-drawer'
                                    }
                                    showDraw={openDrawer}
                                    onClose={() => setOpenDrawer(false)}
                                    closeButtonAriaLabel={
                                        recallsContent.closeDrawerAriaLabel
                                    }
                                >
                                    <RecallVinInput
                                        content={recallsContent}
                                        onSelect={setCurrentVehicle}
                                        closeDrawer={() => setOpenDrawer(false)}
                                    />
                                </FMDrawer>
                                {breadcrumbCondition && (
                                    <Breadcrumbs
                                        items={[
                                            {
                                                name:
                                                    appInformationContent?.breadcrumbStart ||
                                                    appInformationContent?.applicationName,
                                                href: currentRoot,
                                            },

                                            {
                                                name:
                                                    recallsContent?.breadcrumbLabel,
                                                href: '',
                                            },
                                        ]}
                                    />
                                )}

                                <div className="recalls-title-description">
                                    <div className="recalls-title-row">
                                        <div className="recalls-title">
                                            {recallsContent.recallsTitle}
                                        </div>
                                        {recallsContent.recallsLastUpdatedText && (
                                            <div className="recalls-last-updated">
                                                {recallsContent.recallsLastUpdatedText +
                                                    ' ' +
                                                    getCurrentDate()}
                                            </div>
                                        )}
                                    </div>
                                    {!isLoggedIn && (
                                        <div className="recalls-description">
                                            {recallsContent.recallsDescription}
                                        </div>
                                    )}
                                </div>
                                <RecallSignInAndVinInput
                                    content={recallsContent}
                                    profile={profile}
                                    openDrawer={setDrawerOpen}
                                    currentVehicle={
                                        currentVehicle.vehicleAttributes
                                    }
                                    setCurrentVehicle={setCurrentVehicle}
                                    handleAuthVehicleChange={
                                        handleAuthVehicleChange
                                    }
                                    vehicles={vehiclesData}
                                    isLoggedIn={isLoggedIn}
                                />
                            </>
                        )}
                        {landWithVehicleLoader ? (
                            <ActivityIndicator className="recallllls-loader" />
                        ) : (
                            <RecallsInformationSection
                                vehicle={currentVehicle}
                                recallsContent={recallsContent}
                                recallsDataResponse={recallsData}
                                isWebview={isWebview}
                                showRecalls={showRecalls}
                                showCsp={showCsp}
                            />
                        )}

                        {!isWebview && (
                            <>
                                {recallsRecentLinksContent && (
                                    <RecentRecallsLinks
                                        recentRecallsLinksData={
                                            recallsRecentLinksContent
                                        }
                                    />
                                )}

                                {faqs && (
                                    <BlueCruiseFaqs
                                        faqs={faqs}
                                        handleClick={recallsFaqEvent}
                                    />
                                )}
                            </>
                        )}
                        {searchContent && helpCardsContent && (
                            <PreImagineEscapeHatch
                                searchContent={searchContent}
                                helpCardsContent={helpCardsContent}
                                searchTitleForRecalls="Still need help? Contact us"
                                searchBarHelperTextForRecalls="If you don't find what you need, get in touch with customer service."
                            />
                        )}
                    </div>
                </SupportFdsWrapper>
            ) : (
                <PageNotFoundView />
            )}
        </>
    );
};

export default RecallsUnited;
